import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  HiOutlineCheck,
  HiOutlineClipboardDocument,
  HiOutlineShieldCheck,
} from 'react-icons/hi2';
import cloudApi from '../../../services/cloudApi';
import { copyText } from '../../../utils/clipboard';

// Connect-your-client guide for the MCP integration card. Focused on the MCP
// clients that authenticate with a TOKEN (Claude Code, Cursor, anything that
// speaks Streamable HTTP and can set a header) — Claude and ChatGPT both connect
// by sign-in and have their own cards. When the user created a token in this
// session, `sessionToken` fills the snippets so they're paste-ready; otherwise
// they carry a YOUR_TOKEN placeholder (plaintext tokens are never retrievable
// after creation).

const TABS = [
  { key: 'claude-code', label: 'Claude Code' },
  { key: 'other', label: 'Cursor & other apps' },
  { key: 'prompts', label: 'Example prompts' },
];

// Extraction leads — scraping is the job people connect an agent for; analysis
// and cleanup are what they discover after the first table lands.
const EXAMPLE_PROMPTS = [
  { text: 'Scrape all the products from this store and give me a spreadsheet: example.com', tag: 'Extract' },
  { text: 'Find contact emails and phone numbers for every company in this list of URLs.', tag: 'Extract' },
  { text: 'Scrape just the winter collection from this shop.', tag: 'Extract' },
  { text: 'Extract the name, rating, address, and phone from these Google Maps links.', tag: 'Extract' },
  { text: 'Scrape these product pages every morning and track how prices change.', tag: 'Extract' },
  { text: 'Which products under $50 have a rating above 4.5?', tag: 'Analyze' },
  { text: 'Remove the duplicate rows and drop the empty columns from my leads table.', tag: 'Clean' },
  { text: 'How do credits work? What does one credit get me?', tag: 'Learn' },
];

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      className="flex-shrink-0 inline-flex items-center gap-1.5 px-2 py-1 text-[10px] font-medium rounded-lg text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
    >
      {copied
        ? <HiOutlineCheck className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
        : <HiOutlineClipboardDocument className="w-3.5 h-3.5" />}
      {copied ? 'Copied!' : 'Copy'}
    </button>
  );
};

const SnippetBlock = ({ label, text }) => (
  <div className="rounded-xl border border-gray-200 dark:border-white/[0.07] bg-white dark:bg-gray-900/60 overflow-hidden">
    <div className="flex items-center justify-between px-3.5 py-1.5 bg-gray-50/80 dark:bg-white/[0.03] border-b border-gray-100 dark:border-white/[0.05]">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">{label}</span>
      <CopyButton text={text} />
    </div>
    <pre className="px-3.5 py-3 text-2xs font-mono text-gray-700 dark:text-gray-300 leading-relaxed overflow-x-auto whitespace-pre-wrap break-all">{text}</pre>
  </div>
);

const TokenHint = ({ sessionToken }) => (
  sessionToken ? (
    <p className="flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400">
      <HiOutlineCheck className="w-3.5 h-3.5 flex-shrink-0" />
      Using the token you just created — these snippets are ready to paste.
    </p>
  ) : (
    <p className="text-xs text-gray-400 dark:text-gray-500">
      Snippets use <code className="font-mono">YOUR_TOKEN</code> as a placeholder — create a token on the
      MCP card and it fills in automatically (the create dialog also shows a paste-ready version).
    </p>
  )
);

const PromptChip = ({ prompt }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    copyText(prompt.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      onClick={handleCopy}
      title="Click to copy"
      className="group text-left px-3.5 py-2.5 rounded-xl bg-white dark:bg-white/[0.04] border border-gray-200/70 dark:border-white/[0.06] hover:border-cyan-400/40 dark:hover:border-cyan-400/30 transition-all duration-200"
    >
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-[9px] font-semibold uppercase tracking-wider text-cyan-600 dark:text-cyan-400">{prompt.tag}</span>
        <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
          {copied
            ? <><HiOutlineCheck className="w-3 h-3 text-emerald-500" /> Copied</>
            : <><HiOutlineClipboardDocument className="w-3 h-3" /> Copy</>}
        </span>
      </div>
      <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">&ldquo;{prompt.text}&rdquo;</p>
    </button>
  );
};

const McpSetupGuideModal = ({ isOpen, onClose, sessionToken }) => {
  const [activeTab, setActiveTab] = useState('claude-code');

  if (!isOpen) return null;

  const mcpUrl = cloudApi.getMcpServerUrl();
  const tokenValue = sessionToken || 'YOUR_TOKEN';
  const claudeCodeCommand = `claude mcp add --transport http ultimate-web-scraper ${mcpUrl} --header "Authorization: Bearer ${tokenValue}"`;
  const otherClientsJson = `{
  "mcpServers": {
    "ultimate-web-scraper": {
      "url": "${mcpUrl}",
      "headers": {
        "Authorization": "Bearer ${tokenValue}"
      }
    }
  }
}`;

  // Rendered into <body>: the integration cards use `backdrop-blur`, and a
  // filtered ancestor becomes the containing block for `position: fixed` —
  // which would trap the overlay inside the card instead of covering the
  // viewport (same fix as ConnectClaudeModal).
  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-200 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50 animate-in slide-in-from-bottom-3 duration-200 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200/80 dark:border-gray-800 flex items-center justify-between">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">Connect an AI agent</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">Works with Claude Code, Cursor, and any MCP client</p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 dark:border-gray-800/80 px-6 bg-white dark:bg-gray-900 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors duration-200 ${
                activeTab === tab.key
                  ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50 dark:bg-gray-900/50">
          {activeTab === 'claude-code' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                Run this command in your terminal to add the server to Claude Code, then start{' '}
                <code className="text-xs px-1.5 py-0.5 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 font-mono">claude</code>{' '}
                and ask it about your data:
              </p>
              <SnippetBlock label="Terminal" text={claudeCodeCommand} />
              <TokenHint sessionToken={sessionToken} />
            </div>
          )}

          {activeTab === 'other' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                For Cursor (<span className="font-medium text-gray-800 dark:text-gray-200">Settings → MCP → Add server</span>) and
                other MCP clients that support HTTP servers, add this to your MCP configuration:
              </p>
              <SnippetBlock label="mcp.json" text={otherClientsJson} />
              <TokenHint sessionToken={sessionToken} />
              <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                Connecting Claude on the web or desktop? Use the <span className="font-medium text-gray-600 dark:text-gray-300">Claude</span> card
                instead — it has its own guided setup and doesn't need a token pasted anywhere.
              </p>
            </div>
          )}

          {activeTab === 'prompts' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                The fastest way to see it work: ask for an extraction — name a site or paste some URLs,
                and a clean data table comes back. Click any prompt to copy it:
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {EXAMPLE_PROMPTS.map((prompt) => (
                  <PromptChip key={prompt.text} prompt={prompt} />
                ))}
              </div>
            </div>
          )}

          {/* Security note */}
          <div className="flex gap-3 px-4 py-3.5 mt-6 rounded-xl bg-white dark:bg-gray-800/40 border border-gray-200/60 dark:border-gray-700/40">
            <HiOutlineShieldCheck className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              Tokens grant access to this workspace's cloud data and automations per their access level — treat them
              like passwords and revoke them anytime from the MCP card. Extractions always require an explicit
              confirmation step and consume credits per your plan.
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default McpSetupGuideModal;
